import React from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  buildRouteExplanation,
  type RouteCardViewModel,
  WL_RED,
} from "./routeAnalyzerShared";

type RouteCardProps = {
  route: any;
  idx: number;
  product: string;
  optimization: string;
  visibleRoutes: any[];
  viewModel: RouteCardViewModel;
  onSelect: (idx: number) => void;
  onHoverChange: (routeId: string | null) => void;
  onToggleExpanded: (idx: number) => void;
  onSelectRoute: (route: any, idx: number) => void;
};

export const RouteCard: React.FC<RouteCardProps> = ({
  route,
  idx,
  product,
  optimization,
  visibleRoutes,
  viewModel,
  onSelect,
  onHoverChange,
  onToggleExpanded,
  onSelectRoute,
}) => {
  const {
    routePath,
    riskPct,
    riskColor,
    modeLabel,
    totalCost,
    timeLabel,
    distanceLabel,
    isRecommended,
    isSelected,
    isExpanded,
    modeBadgeBg,
    modeBadgeIconColor,
    ModeIcon,
  } = viewModel;

  return (
    <Box
      data-tutorial-step={idx === 0 ? "route-select-row" : undefined}
      onClick={() => onSelect(idx)}
      onMouseEnter={() => onHoverChange(route.route_id ?? null)}
      onMouseLeave={() => onHoverChange(null)}
      sx={{
        backgroundColor: isSelected ? "#FEF2F2" : "#fff",
        border: isSelected ? "2px solid #EF2D3E" : "2px solid #EAEAEA",
        borderRadius: "12px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        p: 2.5,
        cursor: "pointer",
        transition: "border-color 0.15s, background-color 0.15s, box-shadow 0.15s",
        "&:hover": {
          borderColor: isSelected ? "#EF2D3E" : "#D1D5DB",
          boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: "8px",
              backgroundColor: modeBadgeBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ModeIcon sx={{ fontSize: 24, color: modeBadgeIconColor }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                fontSize: "1rem",
                color: "#000",
                lineHeight: 1.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {routePath}
            </Typography>
            <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "0.75rem", color: "#6B7280", mt: 0.25 }}>
              {[route.product_name || product, route.supplier_name].filter(Boolean).join(" · ")}
            </Typography>
            <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "0.75rem", color: modeBadgeIconColor, fontWeight: 500, mt: 0.25 }}>
              {modeLabel}
            </Typography>
          </Box>
        </Box>
        {isRecommended && (
          <Box
            sx={{
              backgroundColor: WL_RED,
              color: "#fff",
              fontFamily: "'Inter', sans-serif",
              fontSize: "0.6875rem",
              fontWeight: 600,
              px: 1.5,
              py: 0.5,
              borderRadius: "999px",
              whiteSpace: "nowrap",
              flexShrink: 0,
              ml: 1,
            }}
          >
            Recommended
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr 1fr",
          gap: 2,
          mb: 2.5,
          mt: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "0.6875rem", color: "#6B7280", mb: 0.5, letterSpacing: "0.01em" }}>Cost</Typography>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "1.125rem", color: "#000" }}>
            {totalCost}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "0.6875rem", color: "#6B7280", mb: 0.5, letterSpacing: "0.01em" }}>Risk</Typography>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "1.125rem", color: riskColor }}>
            {riskPct}%
          </Typography>
        </Box>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "0.6875rem", color: "#6B7280", letterSpacing: "0.01em" }}>Time</Typography>
            <Tooltip title="Average lead time" placement="top" arrow>
              <InfoOutlinedIcon
                sx={{ fontSize: 12, color: "#9CA3AF", cursor: "help" }}
                aria-label="Average lead time"
              />
            </Tooltip>
          </Box>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "1.125rem", color: "#000" }}>
            {timeLabel}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "0.6875rem", color: "#6B7280", mb: 0.5, letterSpacing: "0.01em" }}>Distance</Typography>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "1.125rem", color: "#000" }}>
            {distanceLabel}
          </Typography>
        </Box>
      </Box>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpanded(idx);
        }}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 4,
          fontFamily: "'Inter', sans-serif",
          fontSize: "0.875rem",
          fontWeight: 500,
          color: WL_RED,
          marginBottom: isExpanded ? 12 : 16,
        }}
      >
        Why this route?
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isExpanded && (
        <Box
          sx={{
            mb: 2.5,
            p: 2,
            backgroundColor: "#F7F7F7",
            borderRadius: "8px",
          }}
        >
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "0.875rem", color: "#374151", lineHeight: 1.6, mb: route.nodes?.length ? 1.5 : 0 }}>
            {buildRouteExplanation({ route, visibleRoutes, optimization, from: routePath, to: "" })}
          </Typography>
          {route.nodes && route.nodes.length > 0 && (
            <Box>
              <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "0.75rem", color: "#6B7280", mb: 0.25 }}>Route Stops</Typography>
              <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "0.8125rem", fontWeight: 500, color: "#111111" }}>
                {routePath}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Button
        variant="contained"
        fullWidth
        data-tutorial-step={idx === 0 ? "route-select-button" : undefined}
        onClick={(e) => {
          e.stopPropagation();
          onSelectRoute(route, idx);
        }}
        sx={{
          fontFamily: "'Inter', sans-serif",
          fontWeight: 500,
          fontSize: "0.875rem",
          textTransform: "none",
          height: 44,
          borderRadius: "8px",
          boxShadow: "none",
          backgroundColor: WL_RED,
          color: "#fff",
          letterSpacing: "0.01em",
          "&:hover": { backgroundColor: "#d42635", boxShadow: "none" },
        }}
      >
        Select Route
      </Button>
    </Box>
  );
};

export const LegendSwatch: React.FC<{
  color: string;
  dashArray?: string;
  label: string;
}> = ({ color, dashArray, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    <Box
      sx={{
        width: 18,
        height: 0,
        borderTop: `3px solid ${color}`,
        borderTopStyle: dashArray ? "dashed" : "solid",
        borderRadius: "999px",
        opacity: 0.95,
      }}
    />
    <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "0.6875rem", color: "#6B7280" }}>
      {label}
    </Typography>
  </Box>
);
